import type { ComplianceCheckStatus } from "./compliance-check";
import type { EffectiveScreeningStatus } from "./compliance-review";
import type { IdentityChangeEntry } from "./identity-verification";
import type { TenantCurrency } from "./tenant-regional";

export type DashboardStats = {
  clients: {
    total: number;
    newThisMonth: number;
    byType: {
      person: number;
      company: number;
    };
  };
  flows: {
    draft: number;
    inProgress: number;
    completed: number;
    completedThisMonth: number;
  };
  signatures: {
    pending: number;
    signed: number;
  };
};

export type DashboardAction = {
  type: "signature" | "stale_flow" | "failed_pdf";
  id: string;
  title: string;
  description: string;
  createdAt: string;
  link?: string;
  /** QL-357 — set on a signature whose link expires within 48 hours. */
  expiresAt?: string;
};

export type DashboardActivity = {
  type:
    | "flow_submitted"
    | "flow_completed"
    | "client_created"
    | "signature_signed"
    | "pdf_generated";
  id: string;
  title: string;
  description: string;
  timestamp: string;
  link?: string;
};

export type DashboardUser = {
  id: string;
  name: string;
};

// ─── Configurable dashboards (QL-357) ───────────────────────

/** The two dashboards: every user's own, and the tenant-wide one for admins. */
export const DASHBOARD_KINDS = ["user", "performance"] as const;
export type DashboardKind = (typeof DASHBOARD_KINDS)[number];

/** The period switch inside the widgets that count over time. */
export const DASHBOARD_PERIODS = ["month", "quarter", "year", "total"] as const;
export type DashboardPeriod = (typeof DASHBOARD_PERIODS)[number];
export const DEFAULT_DASHBOARD_PERIOD: DashboardPeriod = "month";

/** Internal widget keys — stored in the tenant's setup, never shown in the UI. */
export const DASHBOARD_WIDGET_KEYS = [
  // Key figures
  "clients",
  "total_aum",
  "saving_inflow",
  "activities",
  "flows",
  "completed_this_month",
  "pending_signatures",
  // Sections
  "users",
  "pipeline",
  "next_meetings",
  "action_items",
  "recent_activity",
  "signatures_expiring",
  "cash_to_invest",
  "approvals_waiting",
  "review_status",
  "advice_to_check",
  "screening",
  "verified_id",
  "identity_changes",
] as const;
export type DashboardWidgetKey = (typeof DASHBOARD_WIDGET_KEYS)[number];

/** Key figures sit four to a row on top; sections fill a 3-column grid below. */
export type DashboardWidgetZone = "key_figure" | "section";

/** Fixed per widget: key figures are 1/4, sections 1/3, 2/3 or the full row. */
export type DashboardWidgetSize = "1/4" | "1/3" | "2/3" | "full";

/** Permission a widget needs on top of seeing the dashboard. */
export type DashboardWidgetPermission = "compliance";

export type DashboardWidgetDefinition = {
  key: DashboardWidgetKey;
  zone: DashboardWidgetZone;
  size: DashboardWidgetSize;
  /** Dashboards the widget can be switched on for. */
  dashboards: readonly DashboardKind[];
  /** Shown to, and computed for, only users with this permission. */
  requiredPermission: DashboardWidgetPermission | null;
  /** Has the Month · Quarter · Year · Total switch. */
  hasPeriod: boolean;
};

/**
 * The tenant's dashboard setup, stored on the tenant. A dashboard with no list
 * shows today's widgets (DEFAULT_DASHBOARD_WIDGETS).
 */
export type DashboardLayoutConfig = {
  /** Switched-on widgets in dashboard order, key figures and sections mixed. */
  user?: DashboardWidgetKey[];
  performance?: DashboardWidgetKey[];
  /** Cash to invest lists clients with more cash than this, in the tenant currency. */
  cashThreshold?: number;
};

/** One section placed in the 3-column grid. */
export type DashboardGridCell = {
  key: DashboardWidgetKey;
  /** 0-based first column. */
  column: number;
  /** Columns taken: 1, 2 or 3. */
  span: number;
};

/** A row of the section grid; `emptyColumns` is the gap left at its end. */
export type DashboardGridRow = {
  cells: DashboardGridCell[];
  emptyColumns: number;
};

export type DashboardArrangement = {
  keyFigures: DashboardWidgetKey[];
  sectionRows: DashboardGridRow[];
};

/** GET /internal/dashboard-settings — the setup as the Settings page edits it. */
export type DashboardSettingsResponse = {
  /** Switched-on widgets in order (today's widgets while nothing is set up). */
  user: DashboardWidgetKey[];
  performance: DashboardWidgetKey[];
  /** Cash to invest threshold in the tenant currency. */
  cashThreshold: number;
  currency: TenantCurrency;
};

/** PUT /internal/dashboard-settings/:dashboard — the switched-on widgets in order. */
export type UpdateDashboardWidgetsRequest = {
  widgets: DashboardWidgetKey[];
};

/** PATCH /internal/dashboard-settings/cash-threshold */
export type UpdateDashboardCashThresholdRequest = {
  cashThreshold: number;
};

// ─── Money widgets (QL-357) ─────────────────────────────────
// Every amount is in the tenant currency, whatever currency the bank sent.

/** Total AUM: cash and positions in synced accounts, today. */
export type DashboardMoneyFigure = {
  amount: number;
  currency: TenantCurrency;
};

/** Saving Inflow / Year: active savings plans, annualised. */
export type DashboardSavingInflowFigure = DashboardMoneyFigure & {
  /** Active plans the inflow comes from. */
  planCount: number;
};

export type DashboardCashToInvestClient = {
  clientId: string;
  clientName: string;
  cash: number;
  /** Cash as a share (0–1) of the client's cash and positions; null when both are 0. */
  shareOfPortfolio: number | null;
};

/** Cash to invest: the owner's clients with more cash than the threshold. */
export type DashboardCashToInvest = {
  currency: TenantCurrency;
  threshold: number;
  /** Every client over the threshold, not just the ones listed. */
  clientCount: number;
  /** The four with the most cash, most first. */
  clients: DashboardCashToInvestClient[];
};

// ─── Activities and the Users table (QL-357) ────────────────

/**
 * Meetings, calls and notes by the user who logged them, and meaningful flow
 * events by their actor (else the flow's responsible advisor, else the
 * client's owner). Future-dated meetings don't count.
 */
export type DashboardActivityCounts = {
  total: number;
  meetings: number;
  calls: number;
  notes: number;
  flowEvents: number;
};

/** Columns the Users table can be ranked by. */
export const DASHBOARD_USER_SORT_KEYS = [
  "name",
  "aum",
  "savingInflow",
  "activities",
  "meetings",
  "calls",
  "notes",
  "flowEvents",
] as const;
export type DashboardUserSortKey = (typeof DASHBOARD_USER_SORT_KEYS)[number];
export type DashboardSortDirection = "asc" | "desc";

export type DashboardUserRow = {
  userId: string;
  name: string;
  clientCount: number;
  /** Today's AUM and yearly inflow of the user's clients. */
  aum: number;
  savingInflow: number;
  /** Activity columns count the selected period. */
  activities: number;
  meetings: number;
  calls: number;
  notes: number;
  flowEvents: number;
};

/** Users widget: the top users by the chosen column. */
export type DashboardUsersTable = {
  currency: TenantCurrency;
  period: DashboardPeriod;
  sortBy: DashboardUserSortKey;
  sortDir: DashboardSortDirection;
  /** Every user in the tenant, for "Showing 15 of N users". */
  totalUsers: number;
  rows: DashboardUserRow[];
};

// ─── Flow widgets (QL-357) ──────────────────────────────────

/** Pipeline: open flows by stage now, and flows completed in the period. */
export type DashboardPipeline = {
  period: DashboardPeriod;
  draft: number;
  inProgress: number;
  /** Submitted and on to review: PDF, compliance and external review. */
  inReview: number;
  signing: number;
  completed: number;
};

/** A signing link that expires within 48 hours. */
export type DashboardExpiringSignature = {
  signatureOrderId: string;
  flowInstanceId: string;
  clientId: string;
  clientName: string;
  flowName: string | null;
  /** The client's owner — whose flow it is. */
  ownerName: string | null;
  expiresAt: string;
  /** When the signing link was created. */
  sentAt: string;
  link: string;
};

export type DashboardExpiringSignatures = {
  /** Every link expiring within 48 hours, not just the ones listed. */
  total: number;
  /** Soonest first. */
  items: DashboardExpiringSignature[];
};

export type DashboardNextMeeting = {
  meetingId: string;
  clientId: string;
  clientName: string;
  occurredAt: string;
  topic: string;
  /** The meeting has a video room. */
  hasVideo: boolean;
};

// ─── Compliance widgets (QL-357) ────────────────────────────
// Shown to, and computed for, compliance users only; always the whole tenant.

/** A flow stopped at the compliance gate. */
export type DashboardApprovalWaiting = {
  approvalId: string;
  flowInstanceId: string;
  clientId: string;
  clientName: string;
  flowName: string | null;
  requestedByName: string | null;
  requestedAt: string;
  link: string;
};

export type DashboardApprovalsWaiting = {
  total: number;
  /** Longest waiting first. */
  items: DashboardApprovalWaiting[];
};

/** Completed advice at one compliance check status, linking into Flows review. */
export type DashboardReviewStage = {
  status: ComplianceCheckStatus;
  count: number;
  link: string;
};

export type DashboardReviewStatus = {
  period: DashboardPeriod;
  stages: DashboardReviewStage[];
};

/** Completed advice not yet approved. */
export type DashboardAdviceToCheckItem = {
  flowInstanceId: string;
  clientId: string;
  clientName: string;
  flowName: string | null;
  advisorName: string | null;
  completedAt: string;
  checkStatus: ComplianceCheckStatus;
  link: string;
};

export type DashboardAdviceToCheck = {
  period: DashboardPeriod;
  total: number;
  /** Latest completed first. */
  items: DashboardAdviceToCheckItem[];
  /** Flows review, filtered to the same period. */
  link: string;
};

/** PEP and sanctions standing counters, each linking into Clients review. */
export type DashboardScreeningCounter = {
  status: Extract<
    EffectiveScreeningStatus,
    "needs_review" | "incomplete" | "failed" | "not_checked"
  >;
  count: number;
  link: string;
};

export type DashboardScreening = {
  counters: DashboardScreeningCounter[];
};

export type DashboardVerifiedId = {
  totalClients: number;
  verified: number;
  notVerified: number;
  /** Clients review, filtered to clients without a Verified ID. */
  link: string;
};

export type DashboardIdentityChanges = {
  /** Newest first. */
  items: IdentityChangeEntry[];
  link: string;
};

// ─── Dashboard endpoints (QL-357) ───────────────────────────

/** GET /internal/dashboard/layout — the widgets this viewer sees, in order. */
export type DashboardLayoutResponse = {
  dashboard: DashboardKind;
  /** Whose User Dashboard this is; null on the Performance Dashboard. */
  userId: string | null;
  widgets: DashboardWidgetKey[];
  /** For the page header: "Jan Skaathun · 38 clients", "Tenant-wide · 6 users · 161 clients". */
  summary: {
    /** The user's name on a User Dashboard; null on the Performance Dashboard. */
    name: string | null;
    clientCount: number;
    /** Users in the tenant on the Performance Dashboard; null on a User Dashboard. */
    userCount: number | null;
  };
};

/** What each widget's endpoint returns. */
export type DashboardWidgetDataMap = {
  clients: DashboardStats["clients"];
  total_aum: DashboardMoneyFigure;
  saving_inflow: DashboardSavingInflowFigure;
  activities: DashboardActivityCounts;
  flows: DashboardStats["flows"];
  completed_this_month: { count: number };
  pending_signatures: DashboardStats["signatures"];
  users: DashboardUsersTable;
  pipeline: DashboardPipeline;
  next_meetings: DashboardNextMeeting[];
  action_items: DashboardAction[];
  recent_activity: DashboardActivity[];
  signatures_expiring: DashboardExpiringSignatures;
  cash_to_invest: DashboardCashToInvest;
  approvals_waiting: DashboardApprovalsWaiting;
  review_status: DashboardReviewStatus;
  advice_to_check: DashboardAdviceToCheck;
  screening: DashboardScreening;
  verified_id: DashboardVerifiedId;
  identity_changes: DashboardIdentityChanges;
};

/** GET /internal/dashboard/widgets/:key */
export type DashboardWidgetResponse<K extends DashboardWidgetKey = DashboardWidgetKey> = {
  [P in K]: { key: P; data: DashboardWidgetDataMap[P] };
}[K];
